import React, { useRef } from "react";
import { useMsal } from "@azure/msal-react";
import {
  Avatar,
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ContrastOutlined from "@mui/icons-material/ContrastOutlined";
import DarkModeOutlined from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlined from "@mui/icons-material/LightModeOutlined";
import LogoutOutlined from "@mui/icons-material/LogoutOutlined";

import { AppearancePreference } from "../../contexts/ColorModeContext";
import useColorMode from "../../hooks/useColorMode";
import { useRoles } from "../../utils/auth";
import { getInitials, roleLabelFor } from "./account";

export interface AccountMenuProps {
  /** id of the menu element (the account button's aria-controls). */
  id: string;
  /** id of the account button (labels the menu). */
  buttonId: string;
  /** The account button; the menu is open while this is set. */
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

const appearanceOptions: {
  value: AppearancePreference;
  label: string;
  Icon: typeof LightModeOutlined;
}[] = [
  { value: "light", label: "Light", Icon: LightModeOutlined },
  { value: "dark", label: "Dark", Icon: DarkModeOutlined },
  { value: "auto", label: "Auto", Icon: ContrastOutlined },
];

/**
 * Account menu from the top bar: who is signed in (name + role), the Light / Dark / Auto
 * appearance control, and Sign out.
 */
export function AccountMenu({
  id,
  buttonId,
  anchorEl,
  onClose,
}: AccountMenuProps) {
  const { instance } = useMsal();
  const { preference, setPreference } = useColorMode();
  const roles = useRoles();
  const selectedRef = useRef<HTMLButtonElement | null>(null);

  const account = instance.getActiveAccount();
  const name = account?.name || "User";

  const handleSignOut = () => {
    onClose();
    instance.logoutRedirect();
  };

  const handleAppearance = (
    _event: React.MouseEvent<HTMLElement>,
    value: AppearancePreference | null
  ) => {
    if (value) setPreference(value);
  };

  // Keys pressed inside the appearance control stay there, so the menu's typeahead,
  // arrow handling and Tab-to-close don't steal focus. Escape still closes the menu.
  const keepKeysInside = (event: React.KeyboardEvent) => {
    if (event.key !== "Escape") event.stopPropagation();
  };

  // Shift+Tab from Sign out moves back to the previous tab stop (the last appearance
  // toggle), mirroring the forward order. The menu would otherwise treat it like Tab and
  // close, leaving keyboard users no way back to the toggles. Tab (forwards) and Escape
  // still close the menu.
  const handleSignOutKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Tab" && event.shiftKey) event.stopPropagation();
  };

  return (
    <Menu
      id={id}
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      variant="menu"
      autoFocus={false}
      disableAutoFocusItem
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      TransitionProps={{ onEntered: () => selectedRef.current?.focus() }}
      MenuListProps={{ "aria-labelledby": buttonId, disablePadding: true }}
      PaperProps={{
        sx: { width: 300, maxWidth: "calc(100% - 32px)", mt: 1.5 },
      }}
    >
      <Box
        role="none"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2.5,
          px: 4,
          py: 3.5,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            fontSize: "0.75rem",
            fontWeight: 500,
            bgcolor: "primary.main",
            color: "primary.contrastText",
          }}
        >
          {getInitials(account?.name)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{ fontWeight: 500, fontSize: "0.875rem" }}
            noWrap
            title={name}
          >
            {name}
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
            {roleLabelFor(roles)}
          </Typography>
        </Box>
      </Box>

      <Box
        role="group"
        aria-labelledby={`${id}-appearance`}
        onKeyDown={keepKeysInside}
        sx={{ display: "grid", gap: 2, px: 4, py: 3 }}
      >
        <Typography
          id={`${id}-appearance`}
          component="span"
          sx={{
            fontSize: "0.6875rem",
            fontWeight: 500,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "text.secondary",
          }}
        >
          Appearance
        </Typography>
        <ToggleButtonGroup
          exclusive
          fullWidth
          value={preference}
          onChange={handleAppearance}
          aria-labelledby={`${id}-appearance`}
          sx={(theme) => ({
            "& .MuiToggleButton-root": {
              display: "grid",
              justifyItems: "center",
              gap: 0.5,
              py: 2,
              px: 1,
              fontSize: "0.78125rem",
              fontWeight: 400,
              color: theme.palette.text.secondary,
              borderColor: theme.palette.divider,
              "& .MuiSvgIcon-root": { fontSize: 18 },
            },
            "& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover":
              {
                backgroundColor: theme.palette.brandTint,
                color: theme.palette.primary.dark,
                fontWeight: 500,
              },
            "& .MuiToggleButton-root.Mui-focusVisible": {
              outlineOffset: -2,
            },
          })}
        >
          {appearanceOptions.map(({ value, label, Icon }) => (
            <ToggleButton
              key={value}
              value={value}
              ref={preference === value ? selectedRef : undefined}
            >
              <Icon aria-hidden />
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
          Auto matches your device and switches with it.
        </Typography>
      </Box>

      <Divider sx={{ my: "0 !important" }} />

      <MenuItem
        onClick={handleSignOut}
        onKeyDown={handleSignOutKeyDown}
        tabIndex={0}
        sx={{ py: 2.5, px: 4 }}
      >
        <ListItemIcon sx={{ color: "text.disabled" }}>
          <LogoutOutlined fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary="Sign out"
          primaryTypographyProps={{ fontSize: "0.84375rem" }}
        />
      </MenuItem>
    </Menu>
  );
}

export default AccountMenu;
